<template>
  <VDialogV3
    id="order-options-comparison-dialog"
    position="bottom"
    :loading="isLoading"
    hide-footer
  >
    <template #header>
      <div
        v-if="isLoading === false"
        class="title"
        :title="dialogTitle"
      >
        {{ dialogTitle }}
      </div>

      <div class="order-options-comparison-legend">
        <div class="item attention">
          <i/> <span>– имеется расхождение</span>
        </div>
        <div
          v-if="!hasTender"
          class="item future"
        >
          <i/> <span>– предзаказ</span>
        </div>
      </div>
    </template>

    <div
      v-if="isLoading === false"
      class="order-options-comparison-content"
    >
      <template v-if="hasTender === false && isPreorderNeeded">
        <header class="preorders-preheader">
          Количество предзаказов: {{ preorderPositions.length }}
        </header>

        <PreorderPositionTable
          :positions="preorderPositions"
          :instance-of-abstract-demand="instanceOfAbstractDemand"
          @save="onSave"
        />
      </template>

      <header class="offers-preheader">
        Количество предложений: {{ orderOptions.length }}
      </header>

      <OrderOptionsTable
        :order-options="orderOptions"
        :instance-of-abstract-demand="instanceOfAbstractDemand"
        @save="onSave"
      />
    </div>
  </VDialogV3>
</template>

<style lang="scss" src="./OrderOptionsComparisonDialog.scss"/>

<script lang="ts">
import type Unit                from "@/domains/Units/Unit";
import type PreorderPosition    from "@/domains/Preorders/PreorderPosition";
import type AbstractOrderOption from "@/domains/Abstract/AbstractOrderOption";
import Demand                   from '@/domains/Demand/Demand';
import DemandsService           from '@/domains/Demand/DemandsService';
import Tender                   from '@/domains/Tenders/Tender';
import Offer                    from '@/domains/Offers/Offer';
import TendersService           from '@/domains/Tenders/TendersService';
import OffersService            from '@/domains/Offers/OffersService';
import PreordersService         from '@/domains/Preorders/PreordersService';
import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import PreorderPositionTable
  from "@/domains/Demand/components/OrderOptionsComparisonDialog/PreorderPositionTable.vue";
import OrderOptionsTable
  from "@/domains/Demand/components/OrderOptionsComparisonDialog/OrderOptionsTable.vue";

/**
 * Сравнение вариантов поставки с требованием: предложения поставщиков либо
 * ставки тендера, плюс чужие предзаказы, к которым можно присоединиться.
 *
 * Диалог только грузит данные и раскладывает их по двум таблицам —
 * сами правила сравнения живут в сущностях, а расхождения выводятся
 * атрибутом data-mismatch.
 */
export default defineComponent({
  name: 'OrderOptionsComparisonDialog',

  components: {
    PreorderPositionTable,
    OrderOptionsTable,
  },

  props: {
    /** Потребность и тендер приходят идентификаторами — сущности целиком
     *  грузит init: диалог открывают из реестров, где лежат усечённые
     *  объекты без ставок, разрешений и характеристик. */
    demandId: {
      type:    Number,
      default: null,
    },

    tenderId: {
      type:    Number,
      default: null,
    },

    showOnlyMineOrderOptionAsSeller: {
      type:    Boolean,
      default: false,
    },
  },

  emits: ['close', 'save'],

  data() {
    return {
      /** Пока идёт загрузка, сущностей нет и рендерить нечего. */
      isLoading: true,

      /** Сущности ещё не загружены — вопрос не задавался, потому undefined. */
      demand: undefined as unknown as Demand,
      tender: undefined as unknown as Tender,

      /** Предложения и ставки — что именно, решает наличие тендера. */
      orderOptions:      [] as AbstractOrderOption[],
      preorderPositions: [] as PreorderPosition[],
    };
  },

  computed: {
    hasTender(): boolean {
      return this.$core.utils.isObject(this.tender);
    },

    hasDemand(): boolean {
      return this.$core.utils.isObject(this.demand);
    },

    /** Эталон для сравнения: тендер или потребность. Оба наследуют
     *  AbstractDemand и несут всё, с чем сверяются предложения. */
    instanceOfAbstractDemand(): Tender | Demand {
      if (this.hasTender) {
        return this.tender;
      }
      else {
        return this.demand;
      }
    },

    isPreorderNeeded(): boolean {
      if (this.hasTender) {
        return this.tender.isPreorderNeeded;
      }
      else {
        return this.hasDemand && this.demand.isPreorderNeeded;
      }
    },

    category(): any {
      if (this.hasTender) {
        return ProductCategoriesService.getProductCategoriesMap().get(this.tender.productCategory.id);
      }
      else {
        return ProductCategoriesService.getProductCategoriesMap().get(this.demand.product.category!.id);
      }
    },

    /** Товар — источник всех требований: производители, разрешения на
     *  поставку, остаточный срок годности, рамки характеристик. */
    product(): any {
      if (this.hasTender) {
        return this.tender.product;
      }
      else {
        return this.demand.product;
      }
    },

    /** Единица измерения потребности. Объект, а не имя: его принимает
     *  Qty.format, который сам клеит постфикс неразрывным пробелом. */
    demandUnit(): Unit | null {
      if (this.hasTender) {
        return this.tender.unit;
      }
      else if (this.hasDemand) {
        return this.demand.product.unit;
      }
      else {
        return null;
      }
    },

    dialogTitle(): string {
      let result = this.category.name;

      /** Продавец видит только категорию */
      if (this.$core.currentPartner?.isBuyer !== true) {
        return result;
      }

      if (this.product.code) {
        result += ` (арт. ${this.product.code})`;
      }
      else {
        result += ' (артикул не указан)';
      }

      if (this.isPreorderNeeded) {
        result += ` ${this.instanceOfAbstractDemand.preorderShipmentPeriod.getStringRepresentation()}`;
      }
      else {
        result += ` к ${this.$core.utils.date.humanizeDate(this.instanceOfAbstractDemand.desiredReceiptDate, null).toLowerCase()}`;
      }

      if (this.hasTender) {
        /** Ветвимся по факту заказа, а не по сравнению остатка с исходным
         *  количеством: Qty — объект, и === сравнил бы ссылки, а не числа. */
        if (this.tender.qtyOrdered && this.tender.qtyOrdered.isPositive()) {
          const qtyLeft = this.tender.qtyLeft.minus(this.tender.qtyOrdered);

          result += ` - ${qtyLeft.format(this.demandUnit)}`
            + `  (всего ${this.tender.qtyDesired.format(this.demandUnit)})`;
        }
        else {
          result += ` - ${this.tender.qtyLeft.format(this.demandUnit)}`;
        }
      }
      else if (this.hasDemand) {
        if (this.demand.qtyLeft.equals(this.demand.qtyDesired)) {
          result += ` - ${this.demand.qtyDesired.format(this.demandUnit)}`;
        }
        else {
          result += ` - ${this.demand.qtyLeft.format(this.demandUnit)}`
            + `  (всего ${this.demand.qtyDesired.format(this.demandUnit)})`;
        }
      }

      return result;
    },

  },

  created() {
    this.init();
  },

  methods: {
    /**
     * Загружает то, из чего открыли диалог: тендер со ставками либо
     * потребность с предложениями. Зовётся заново после покупки —
     * остатки и ставки к этому моменту уже другие.
     */
    async init(): Promise<void> {
      this.isLoading = true;

      if (this.tenderId) {
        await this.loadFromTender();
      }
      else {
        await this.loadFromDemand();
      }

      this.isLoading = false;
    },

    async loadFromTender(): Promise<void> {
      const { tender } = await TendersService.get(this.tenderId, null, this.$core.timeZone);

      this.tender = tender;

      if (this.showOnlyMineOrderOptionAsSeller) {
        if (this.tender.currentPartnerBid) {
          this.orderOptions = [this.tender.currentPartnerBid];
        }
        else {
          this.orderOptions = [];
        }
      }
      else {
        this.orderOptions = this.tender.bids;
      }
    },

    async loadFromDemand(): Promise<void> {
      this.demand = await DemandsService.get(this.demandId);

      if (this.demand.isPreorderNeeded) {
        this.preorderPositions = await this.loadPreorders();
      }

      this.orderOptions = await this.loadOffers();
    },

    /** Загружает предложения по категории через сервис (отдаёт готовые Offer) */
    async loadOffers(): Promise<Offer[]> {
      const params: Record<string, any> = {
        requireNestedObjects: true,
        productCategory:      { id: this.category.id },
        sort:                 { by: 'price', direction: 'asc' },
      };

      if (this.hasDemand && this.demand.isPreorderNeeded === true) {
        params.isAcceptingPreorder = true;
      }

      return await OffersService.getList(params);
    },

    /** Загружает позиции предзаказов по товару через сервис (отдаёт готовые PreorderPosition) */
    async loadPreorders(): Promise<PreorderPosition[]> {
      return await PreordersService.getList({
        requireNestedObjects: true,
        requireOffers:        true,
        product:              { id: this.product.id },
        isFinished:           false,
        sort:                 { by: 'price', direction: 'asc' },
      });
    },

    /** Что-то купили: остатки и ставки уже другие, перечитываем. */
    onSave(): void {
      this.init();

      /** Некоторые родители хотят знать о сохранении сущности */
      this.$emit('save');
    },

  },
});
</script>